import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import axios from 'axios';
import { 
  Trophy, 
  TrendingUp, 
  Users, 
  Calendar,
  Eye,
  ArrowRight,
  Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeaturedTournaments = ({ period = 'week', limit = 5 }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedTournaments();
  }, [period]);

  const fetchFeaturedTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments/featured?period=${period}&limit=${limit}`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to fetch featured tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (tournaments.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black">
              {period === 'week' ? 'TRENDING THIS WEEK' : 'TOURNAMENT OF THE MONTH'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Most popular tournaments based on views and registrations
            </p>
          </div>
        </div>
        <TrendingUp className="w-6 h-6 text-orange-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tournaments.map((tournament, index) => (
          <Card 
            key={tournament.id} 
            className={`card-hover overflow-hidden relative ${index === 0 ? 'border-2 border-orange-500' : ''}`}
          >
            {/* Featured Badge for #1 */}
            {index === 0 && (
              <div className="absolute top-4 right-4 z-10">
                <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0">
                  <Trophy className="w-3 h-3 mr-1" />
                  #1 Trending
                </Badge>
              </div>
            )}
            
            {/* Rank Badge */}
            {index > 0 && (
              <div className="absolute top-4 right-4 z-10">
                <Badge variant="secondary" className="font-bold">
                  #{index + 1}
                </Badge>
              </div>
            )}

            <div className={`h-2 ${index === 0 ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-primary to-accent'}`} />
            
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between mb-2">
                <Badge variant="outline" className={getStatusColor(tournament.status)}>
                  {tournament.status.toUpperCase()}
                </Badge>
                <Badge variant="secondary">
                  {tournament.age_category} • {tournament.gender}
                </Badge>
              </div>
              <CardTitle className="text-xl font-bold line-clamp-2">{tournament.name}</CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground line-clamp-2">
                {tournament.description}
              </p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs">{formatDate(tournament.start_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs">{tournament.registered_teams}/{tournament.max_teams}</span>
                </div>
              </div>

              {/* Popularity Metrics */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                <div className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  <span>{tournament.views || 0} views</span>
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>{tournament.registered_teams} teams</span>
                </div>
              </div>
              
              <Link to={`/tournaments/${tournament.id}`}>
                <Button className="w-full gap-2" variant={index === 0 ? "default" : "outline"}>
                  View Details
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
